"use client";

import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/design-system/input";
import { useToast } from "@/components/ui/use-toast";
import { trpc } from "@/lib/trpc-client";
import type { CreatorPortalAccess } from "./creatorAuthContext";
import { handleFieldChanged, syncHandleFields } from "./handleFieldSync";
import { FIELD } from "./one/chrome";
import { SettingRow } from "./settingsRow";

/**
 * Where a creator fixes the social handles we have on file.
 *
 * These are not profile decoration: the TikTok handle names the Spark ads a
 * submission launches as, and the Instagram handle is the account brand
 * partnership requests are sent to — a typo in either fails much later and
 * looks like something else. They were only ever collected once, on the /join
 * form, and the only way to change one was to ask the team.
 *
 * The handles are global to the creator rather than per-program, which is why
 * this sits on the portal home rather than inside any one program's card.
 *
 * FIELDS, NOT A SECTION. This was a pane on Home and then a section on Home,
 * and both were the same mistake at different sizes: a form that is used once
 * and then never again was sitting permanently in the middle of a page about
 * this week's work. It reads as settings because it is settings, so it lives
 * in the settings sheet.
 *
 * TWO ROWS, NOT A GROUP WITH A PREAMBLE. The pair used to open with two
 * sentences about how ads are named and where partnership requests go, above
 * the fields they described. Each row now carries its own reason in five
 * words, under its own label — so nothing has to be read before the thing it
 * explains, and the two rows are the same object as every other row in the
 * sheet rather than a form embedded in one.
 *
 * A HOOK, BECAUSE THE BUTTON LEFT THE ROWS. Save sits in the sheet's footer
 * now, which is a different part of the tree from the fields it commits — so
 * what used to be one component is the rows plus the state a footer needs to
 * drive them. The caller renders both; nothing here knows where either lands.
 */

export function useCreatorHandles({
  access,
  tiktokHandle,
  instagramHandle,
}: {
  access: CreatorPortalAccess;
  tiktokHandle: string | null;
  instagramHandle: string | null;
}): {
  /** The two rows, for the sheet's body. */
  rows: ReactNode;
  /** Nothing has been typed since the last thing the server told us. */
  clean: boolean;
  saving: boolean;
  submit: () => void;
} {
  const t = useTranslations("home");
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [tiktok, setTiktok] = useState(tiktokHandle ?? "");
  const [instagram, setInstagram] = useState(instagramHandle ?? "");
  /** What the server last told us each field was — see handleFieldSync.ts. */
  const synced = useRef({
    tiktok: tiktokHandle ?? "",
    instagram: instagramHandle ?? "",
  });

  // This card stays mounted across a background `getPortalHome` refetch, so
  // without following the server a staff correction made in the meantime would
  // leave stale values on screen — which then read as an edit and would be
  // written straight back over the correction on Save. Resolved from the
  // values in hand rather than through `setState` updaters: an updater would
  // run after the line below has already advanced `synced`, and so would
  // compare each field against the new value and call it touched.
  useEffect(() => {
    const next = {
      tiktok: tiktokHandle ?? "",
      instagram: instagramHandle ?? "",
    };
    const resolved = syncHandleFields({
      current: { tiktok, instagram },
      synced: synced.current,
      next,
    });
    synced.current = next;
    setTiktok(resolved.tiktok);
    setInstagram(resolved.instagram);
  }, [tiktokHandle, instagramHandle]);

  const save = trpc.creatorPortal.updateMyHandles.useMutation({
    onSuccess: (updated, submitted) => {
      // The inputs stay editable while a save is in flight, so a creator can
      // spot a typo and keep fixing it. Merged the same way a refetch is,
      // against what this request actually sent: a field untouched since then
      // takes the server's value — which may be normalized, so the stored
      // handle is what they see — while one they have since edited keeps
      // their text instead of snapping back to what was submitted.
      const next = {
        tiktok: updated.tiktokHandle ?? "",
        instagram: updated.instagramHandle ?? "",
      };
      const resolved = syncHandleFields({
        current: { tiktok, instagram },
        synced: {
          // A field this request didn't carry counts as untouched, so it
          // follows the server — that is how a staff correction to the other
          // handle reaches the screen instead of being held back.
          tiktok: submitted.tiktokHandle ?? synced.current.tiktok,
          instagram: submitted.instagramHandle ?? synced.current.instagram,
        },
        next,
      });
      synced.current = next;
      setTiktok(resolved.tiktok);
      setInstagram(resolved.instagram);
      void utils.creatorPortal.getPortalHome.invalidate();
      // The Meta link card on Your videos reads the same Instagram handle, so
      // a correction here has to reach it rather than wait for a reload.
      void utils.creatorPortal.getMyMetaLink.invalidate();
      toast({ description: t("handles.saved") });
    },
    onError: (error) =>
      toast({ description: error.message, variant: "destructive" }),
  });

  // Compared against the last values the server gave us rather than the props,
  // which lag behind a successful save until the refetch lands — comparing
  // against them flickered Save back on straight after saving.
  const changed =
    handleFieldChanged(tiktok, synced.current.tiktok) ||
    handleFieldChanged(instagram, synced.current.instagram);

  return {
    clean: !changed,
    saving: save.isPending,
    submit: () =>
      save.mutate({
        ...access,
        // Only what actually changed. The server leaves an omitted field
        // alone, so a staff correction to the handle this creator isn't
        // editing can't be overwritten by the value the page loaded before
        // that correction landed. An emptied field still sends, as an empty
        // string, which is how it gets cleared.
        ...(handleFieldChanged(tiktok, synced.current.tiktok)
          ? { tiktokHandle: tiktok }
          : {}),
        ...(handleFieldChanged(instagram, synced.current.instagram)
          ? { instagramHandle: instagram }
          : {}),
      }),
    rows: (
      <>
        <SettingRow
          label={t("handles.tiktok.label")}
          hint={t("handles.tiktok.hint")}
          htmlFor="creator-tiktok-handle"
          control={
            <Input
              id="creator-tiktok-handle"
              className={FIELD}
              value={tiktok}
              placeholder={t("handles.placeholder")}
              autoComplete="off"
              onChange={(event) => setTiktok(event.target.value)}
            />
          }
        />

        <SettingRow
          label={t("handles.instagram.label")}
          hint={t("handles.instagram.hint")}
          htmlFor="creator-instagram-handle"
          control={
            <Input
              id="creator-instagram-handle"
              className={FIELD}
              value={instagram}
              placeholder={t("handles.placeholder")}
              autoComplete="off"
              onChange={(event) => setInstagram(event.target.value)}
            />
          }
        />
      </>
    ),
  };
}
